/**
 * 대시보드 레이아웃 유틸리티 함수들
 */
import { calculateRowHeight } from './dashboardStyleUtils';

/**
 * 새 차트를 위한 최적의 위치를 찾는 함수
 * @param {Array} currentLayout - 현재 레이아웃 배열
 * @param {number} newWidth - 새 컴포넌트의 너비
 * @param {number} newHeight - 새 컴포넌트의 높이
 * @returns {Object} 최적의 위치 객체 {x, y, w, h}
 */
export const findBestPositionForNewChart = (currentLayout, newWidth, newHeight) => {
  // 항상 가장 아래에 새 차트를 배치
  console.log(`[findBestPositionForNewChart] 현재 레이아웃:`, currentLayout);
  console.log(`[findBestPositionForNewChart] 레이아웃 항목 수: ${currentLayout.length}`);
  
  let maxBottom = 0;
  currentLayout.forEach(item => {
    const bottom = item.y + item.h;
    console.log(`[findBestPositionForNewChart] 아이템 ${item.i}: y=${item.y}, h=${item.h}, bottom=${bottom}`);
    if (bottom > maxBottom) {
      maxBottom = bottom;
    }
  });
  
  console.log(`[findBestPositionForNewChart] 계산된 maxBottom: ${maxBottom}`);
  const result = { x: 0, y: maxBottom, w: newWidth, h: newHeight };
  console.log(`[findBestPositionForNewChart] 반환할 위치:`, result);
  
  return result;
};

/**
 * 새로 추가된 컴포넌트의 위치로 부드럽게 스크롤하는 함수
 * @param {Object} componentPosition - 컴포넌트 위치 정보 {x, y, w, h}
 * @param {Object} dashboardTemplate - 대시보드 템플릿
 * @param {number} containerWidth - 컨테이너 너비
 * @param {number} delay - 스크롤 지연 시간 (ms)
 */
export const scrollToNewComponent = (componentPosition, dashboardTemplate, containerWidth = 1200, delay = 300) => {
  if (!componentPosition) return;

  setTimeout(() => {
    try {
      // 대시보드 컨테이너 찾기
      const dashboardContainer = document.querySelector('.dashboard-container');
      if (!dashboardContainer) {
        console.warn('[scrollToNewComponent] 대시보드 컨테이너를 찾을 수 없습니다.');
        return;
      }

             // 행 높이 계산
       const rowHeight = calculateRowHeight(dashboardTemplate, containerWidth);
       const gridSpacing = dashboardTemplate?.dashboard_options?.grid_spacing ?? 10;

      // 새 컴포넌트의 실제 픽셀 위치 계산
      const componentY = componentPosition.y * (rowHeight + gridSpacing);
      const componentHeight = componentPosition.h * (rowHeight + gridSpacing);
      
      // 스크롤 대상 위치 계산 (컴포넌트 중앙이 화면 중앙에 오도록)
      const containerHeight = dashboardContainer.clientHeight;
      const targetScrollTop = componentY + (componentHeight / 2) - (containerHeight / 2);
      
      // 최소/최대 스크롤 위치 제한
      const maxScrollTop = dashboardContainer.scrollHeight - containerHeight;
      const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

      console.log('[scrollToNewComponent] 스크롤 정보:', {
        componentPosition,
        rowHeight,
        gridSpacing,
        componentY,
        componentHeight,
        containerHeight,
        targetScrollTop,
        finalScrollTop,
        maxScrollTop
      });

      // 부드러운 스크롤 실행
      dashboardContainer.scrollTo({
        top: finalScrollTop,
        behavior: 'smooth'
      });

      console.log(`[scrollToNewComponent] 새 컴포넌트 (${componentPosition.x}, ${componentPosition.y})로 스크롤 완료`);
      
    } catch (error) {
      console.error('[scrollToNewComponent] 스크롤 중 오류:', error);
    }
  }, delay);
};

/**
 * 템플릿 구조를 일관된 형태로 정규화하는 함수
 * @param {Object} template - 정규화할 템플릿 객체
 * @returns {Object} 정규화된 템플릿 객체
 */
export const normalizeTemplateStructure = (template) => {
  // REMOVE: console.log('[Dashboard] 템플릿 정규화 시작:', JSON.stringify(template)); // 전체 template 로깅
  
  // 템플릿이 없는 경우
  if (!template || typeof template !== 'object') {
    console.warn('[Dashboard] 정규화 대상 템플릿이 없음');
    return {
      components: [],
      layout: { columns: 12 },
      dashboard_options: {}
    };
  }
  
  // 이미 정규화된 경우 (최상위에 components와 dashboard_options가 있는 경우)
  if (template.components && Array.isArray(template.components)) {
    // REMOVE: console.log('[Dashboard] 이미 정규화된 템플릿 구조');
    return {
      ...template,
      // components 배열 필터링 추가
      components: (template.components || []).filter(comp => comp && comp.id && typeof comp.id === 'string'),
      dashboard_options: template.dashboard_options || {},
      groups: template.groups || [] // ⭐ groups 보존!
    };
  }
  
  // 중첩된 템플릿 구조 확인 (template.template)
  if (template.template && typeof template.template === 'object') {
    // REMOVE: console.log('[Dashboard] 중첩된 템플릿 구조 발견, 정규화 시도');
    
    // 중첩된 내부 템플릿에 components가 있는 경우
    if (template.template.components && Array.isArray(template.template.components)) {
      // REMOVE: console.log('[Dashboard] 중첩 구조에서 components 발견:', template.template.components.length);
      
      // 정규화된 템플릿 생성
      const normalizedTemplate = {
        ...template,
        // components 배열 필터링 추가
        components: (template.template.components || []).filter(comp => comp && comp.id && typeof comp.id === 'string'),
        layout: template.template.layout || template.layout || { columns: 12 },
        dashboard_options: template.template.dashboard_options || template.dashboard_options,
        groups: template.template.groups || template.groups || [] // ⭐ groups 복사!
      };
      
      // 중첩된 template 제거
      delete normalizedTemplate.template;
      
      // REMOVE: console.log('[Dashboard] 정규화된 템플릿:', normalizedTemplate);
      return normalizedTemplate;
    }
    
    // 더 깊은 중첩 확인 (template.template.template)
    if (template.template.template && typeof template.template.template === 'object') {
      // REMOVE: console.log('[Dashboard] 2중 중첩 구조 발견, 더 깊은 정규화 시도');
      
      // 재귀적으로 정규화 수행
      const innerNormalized = normalizeTemplateStructure(template.template);
      
      const normalizedTemplate = {
        ...template,
        // components 배열 필터링 추가
        components: (innerNormalized.components || []).filter(comp => comp && comp.id && typeof comp.id === 'string'),
        layout: innerNormalized.layout,
        dashboard_options: innerNormalized.dashboard_options || template.dashboard_options,
        groups: innerNormalized.groups || template.groups || [] // ⭐ groups 복사!
      };
      
      // 중첩된 template 제거
      delete normalizedTemplate.template;
      
      // REMOVE: console.log('[Dashboard] 깊은 중첩 정규화 결과:', normalizedTemplate);
      return normalizedTemplate;
    }
  }
  
  // 문자열로 된 템플릿 필드 처리 (이전 코드 개선)
  if (template.template && typeof template.template === 'string') {
    try {
      // 문자열을 파싱하여 중첩 구조를 확인
      const parsedTemplate = JSON.parse(template.template);
      // REMOVE: console.log('[Dashboard] 문자열 템플릿을 파싱하여 정규화 시도');
      
      // 파싱된 템플릿을 정규화
      const normalizedFromParsed = normalizeTemplateStructure({
        ...template,
        template: parsedTemplate
      });
      
      // REMOVE: console.log('[Dashboard] 문자열 파싱 후 정규화 결과:', normalizedFromParsed);
      return normalizedFromParsed;
    } catch (error) {
      console.error('[Dashboard] 템플릿 문자열 파싱 실패:', error);
    }
  }
  
  // 적절한 구조를 찾지 못한 경우 빈 템플릿 반환
  console.warn('[Dashboard] 정규화 실패, 기본 템플릿 사용');
  return {
    ...template, // 원본 template의 다른 필드는 유지할 수 있도록
    components: [], // components는 확실히 빈 배열로 초기화
    layout: template.layout || { columns: 12 }, // layout도 기본값 또는 기존값 사용 (정사각형 그리드)
    dashboard_options: template.dashboard_options || {}, // dashboard_options도 기본값 또는 기존값 사용
    groups: template.groups || [] // ⭐ groups 보존!
  };
}; 